import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, LayoutGrid, Table2, Phone, Building2, ChevronDown, ChevronUp, AlertTriangle, Send, UploadCloud, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDivision } from "@/context/DivisionContext";
import { useDropdowns } from "@/hooks/useMeta";
import { LEAD_STATUSES, LEAD_STATUS_COLOR, PRODUCT_TYPES, formatDate, canDeleteLeadDeal, VOICE_FEATURES_ENABLED } from "@/lib/constants";
import { PageHeader } from "@/components/PageHeader";
import { KanbanBoard } from "@/components/KanbanBoard";
import { StageInfo } from "@/components/StageInfo";
import { StatusBadge, DivisionTag } from "@/components/StatusBadge";
import { LeadFormDialog } from "@/components/LeadFormDialog";
import { ScanCardButton } from "@/components/ScanCardDialog";
import { EventFilter } from "@/components/EventFilter";
import { SendCataloguesDialog } from "@/components/SendCataloguesDialog";
import { AddToBrevoDialog } from "@/components/AddToBrevoDialog";
import { VoiceLeadButton } from "@/components/VoiceLeadButton";
import { VoiceGuidedIntake, speechSupported } from "@/components/VoiceGuidedIntake";
import { LeadStatusSelect } from "@/components/LeadStatusSelect";
import { ReasonDialog } from "@/components/ReasonDialog";
import { CompletenessBadge } from "@/components/CompletenessBadge";
import { AssignOwnerSelect } from "@/components/AssignOwnerSelect";
import { DeleteButton } from "@/components/DeleteButton";
import { BulkImport } from "@/components/BulkImport";
import { ExportButton } from "@/components/ExportButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SalespersonFilter } from "@/components/SalespersonFilter";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

function LeadCard({ lead, isGlobal, onClick, user, onDelete }) {
  return (
    <div className="p-3" onClick={onClick} data-testid={`lead-card-${lead.id}`} role="button">
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium text-sm text-slate-900 leading-snug">{lead.project_name || lead.company}</p>
        <span className="flex items-center gap-1">
          <CompletenessBadge completeness={lead.completeness} compact />
          {lead.rating && lead.rating !== "None" && <StatusBadge kind="rating" value={lead.rating} />}
        </span>
      </div>
      {lead.sla_state === "breached" && <div className="mt-1.5"><StatusBadge kind="sla" value="breached" /></div>}
      <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1.5">
        <Building2 className="h-3 w-3" /> {lead.company}
      </div>
      {lead.phone && <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-1"><Phone className="h-3 w-3" /> {lead.phone}</div>}
      <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-slate-100">
        <span className="text-[11px] text-slate-400">{lead.owner_name}</span>
        <div className="flex items-center gap-1">
          {isGlobal && <DivisionTag id={lead.division_id} />}
          {canDeleteLeadDeal(user) && <DeleteButton testId={`lead-card-delete-${lead.id}`} title="Delete this lead?" description="This permanently removes the lead and its activity history." onConfirm={() => onDelete(lead.id)} />}
        </div>
      </div>
    </div>
  );
}

const LEAD_BREAKDOWN_TABLES = [
  { key: "total", title: "Total leads", cols: [["total", "Total Leads", ""]] },
  { key: "status", title: "By status", cols: [["New", "New", ""], ["Contacted", "Contacted", ""], ["Nurturing", "Nurturing", ""], ["Qualified", "Qualified", ""], ["Lead Lost", "Lead Lost", ""]] },
  { key: "rating", title: "By rating", cols: [["Hot", "Hot", "text-red-600"], ["Warm", "Warm", "text-amber-600"], ["Cold", "Cold", "text-sky-600"]] },
];

function LeadBreakdownTable({ rows, cols, testid, showNames }) {
  return (
    <div className="border border-slate-200 rounded-md bg-white shrink-0" data-testid={testid}>
      <table className="text-sm">
        <thead>
          <tr className="bg-slate-50/70 text-xs uppercase tracking-wider text-slate-500">
            {showNames && <th className="text-left font-medium px-3 py-2">Salesperson</th>}
            {cols.map(([k, label, cls]) => <th key={k} className={`text-right font-medium px-3 py-2 ${cls}`}>{label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.owner_id || r.name} className="border-t border-slate-100" data-testid={`${testid}-row-${r.name.replace(/\s+/g, "-")}`}>
              {showNames && <td className="px-3 py-1.5 font-medium text-slate-700 whitespace-nowrap">{r.name}</td>}
              {cols.map(([k]) => <td key={k} className={`px-3 py-1.5 text-right font-mono whitespace-nowrap ${k === "total" ? "font-semibold" : ""}`}>{r[k] || 0}</td>)}
            </tr>
          ))}
          <tr className="border-t border-slate-200 bg-slate-50/70 font-semibold">
            {showNames && <td className="px-3 py-1.5">Total</td>}
            {cols.map(([k]) => <td key={k} className="px-3 py-1.5 text-right font-mono">{rows.reduce((s, r) => s + (r[k] || 0), 0)}</td>)}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function LeadOwnerSummary({ rows }) {
  const [expanded, setExpanded] = useState(false);
  if (!rows?.length) return null;
  return (
    <div className="mb-4" data-testid="lead-owner-summary">
      <button type="button" onClick={() => setExpanded((v) => !v)}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-brand hover:underline whitespace-nowrap"
        data-testid="lead-owner-summary-toggle">
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {expanded ? "Hide breakdown by salesperson" : "View breakdown by salesperson"}
      </button>
      {expanded && (
        <div className="mt-2 flex flex-nowrap items-start gap-6 overflow-x-auto" data-testid="lead-owner-summary-table">
          {LEAD_BREAKDOWN_TABLES.map((t, i) => <LeadBreakdownTable key={t.key} rows={rows} cols={t.cols} showNames={i === 0} testid={`lead-breakdown-${t.key}`} />)}
          <p className="w-full text-[10px] text-slate-400 -mt-3">Counts of open (unconverted) leads · Hot / Warm / Cold = lead rating</p>
        </div>
      )}
    </div>
  );
}

export default function Leads({ dealerView }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { divisionParam, isGlobal } = useDivision();
  const { user } = useAuth();
  const { data: dd } = useDropdowns();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState("table");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [rating, setRating] = useState("");
  const [region, setRegion] = useState("");
  const [productType, setProductType] = useState("");
  const [owner, setOwner] = useState("");
  const [event, setEvent] = useState(() => params.get("event") || "");
  const [needsDetails, setNeedsDetails] = useState(() => params.get("needs_details") === "1");
  const [sel, setSel] = useState([]);
  const [sendOpen, setSendOpen] = useState(false);
  const [brevoOpen, setBrevoOpen] = useState(false);
  const canBulkEmail = ["marketing", "super_admin", "sales_head", "region_head", "sales_manager"].includes(user?.role);
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [voiceInitial, setVoiceInitial] = useState(null);

  useEffect(() => { if (params.get("new")) { setFormOpen(true); params.delete("new"); setParams(params); } }, [params, setParams]);

  const kanban = useQuery({
    queryKey: ["leads-kanban", divisionParam, !!dealerView],
    queryFn: async () => (await api.get("/leads/kanban", { params: { division_id: divisionParam, dealer_view: dealerView ? 1 : 0 } })).data,
    enabled: view === "kanban",
  });

  const table = useQuery({
    queryKey: ["leads-table", divisionParam, search, status, rating, region, productType, owner, event, needsDetails, page, !!dealerView],
    queryFn: async () => (await api.get("/leads", { params: { division_id: divisionParam, search, status, rating, region, product_type: productType, owner_id: owner, event, needs_details: needsDetails ? 1 : 0, page, page_size: 25, dealer_view: dealerView ? 1 : 0 } })).data,
    enabled: view === "table",
  });
  const items = table.data?.items || [];

  const { data: summary } = useQuery({
    queryKey: ["leads-summary", divisionParam, !!dealerView],
    queryFn: async () => (await api.get("/leads/summary", { params: { division_id: divisionParam, dealer_view: dealerView ? 1 : 0 } })).data,
  });
  const needsCount = useQuery({
    queryKey: ["leads-needs-details", divisionParam, !!dealerView],
    queryFn: async () => (await api.get("/leads", { params: { division_id: divisionParam, needs_details: 1, page_size: 1, dealer_view: dealerView ? 1 : 0 } })).data,
  });

  const [reasonMove, setReasonMove] = useState(null);
  const move = async (id, toStatus, extra = {}) => {
    const kind = toStatus === "Qualified" ? "won" : ["Lead Lost"].includes(toStatus) ? "lost" : null;
    if (kind && !extra.won_reason && !extra.lost_reason) { setReasonMove({ id, toStatus, kind }); return; }
    // optimistic
    qc.setQueryData(["leads-kanban", divisionParam], (old) => {
      if (!old) return old;
      const cols = { ...old.columns };
      let moved;
      for (const k of Object.keys(cols)) {
        const idx = cols[k].findIndex((x) => x.id === id);
        if (idx >= 0) { moved = cols[k][idx]; cols[k] = cols[k].filter((x) => x.id !== id); }
      }
      if (moved) cols[toStatus] = [{ ...moved, lead_status: toStatus }, ...(cols[toStatus] || [])];
      return { ...old, columns: cols };
    });
    try {
      await api.put(`/leads/${id}`, { lead_status: toStatus, ...extra });
      toast.success(`Moved to ${toStatus}`);
    } catch { toast.error("Failed to move"); qc.invalidateQueries({ queryKey: ["leads-kanban"] }); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead deleted");
      qc.invalidateQueries({ queryKey: ["leads-table"] });
      qc.invalidateQueries({ queryKey: ["leads-kanban"] });
    } catch (e) { toast.error(apiError(e)); throw e; }
  };

  const columns = LEAD_STATUSES.map((s) => ({ key: s, title: s, accent: LEAD_STATUS_COLOR[s]?.dot, info: <StageInfo kind="lead" stage={s} /> }));

  return (
    <div>
      <PageHeader title={dealerView ? "Dealer Leads" : "Leads"}
        subtitle={dealerView ? "Leads originated by or involving dealer partners — sales need to allocate these." : "Capture, qualify and assign leads within your division."}>
        <div className="flex rounded-md border border-slate-200 overflow-hidden">
          <button onClick={() => setView("kanban")} className={`px-3 h-9 flex items-center gap-1.5 text-sm ${view === "kanban" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`} data-testid="view-kanban"><LayoutGrid className="h-4 w-4" /> Board</button>
          <button onClick={() => setView("table")} className={`px-3 h-9 flex items-center gap-1.5 text-sm ${view === "table" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`} data-testid="view-table"><Table2 className="h-4 w-4" /> Table</button>
        </div>
        {user?.role === "marketing" && <ScanCardButton />}
        {!["factory", "management", "marketing"].includes(user?.role) && (
          <>
            <ScanCardButton />
            <Button onClick={() => { setVoiceInitial(null); setFormOpen(true); }} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="new-lead-btn"><Plus className="h-4 w-4" /> New Lead</Button>
          </>
        )}
        {VOICE_FEATURES_ENABLED && !["factory", "management", "marketing"].includes(user?.role) && (
          speechSupported() ? (
            <VoiceGuidedIntake mode="lead" onExtracted={(lead) => {
              setVoiceInitial({ lead_status: "New", lead_source: "Self Generated", rating: "None", ...lead });
              setFormOpen(true);
            }} />
          ) : (
            <VoiceLeadButton onExtracted={(lead) => {
              setVoiceInitial({ lead_status: "New", lead_source: "Self Generated", rating: "None", ...lead });
              setFormOpen(true);
            }} />
          )
        )}
        {!["factory", "management", "dealer", "marketing"].includes(user?.role) && (
          <>
            <ExportButton entity="lead" path="/leads/export" divisionParam={divisionParam} />
            <BulkImport entity="lead" templatePath="/leads/import-template" importPath="/leads/import"
              onDone={() => { qc.invalidateQueries({ queryKey: ["leads-kanban"] }); qc.invalidateQueries({ queryKey: ["leads-table"] }); }} />
          </>
        )}
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 whitespace-nowrap" data-testid="leads-total-chip">
          {summary ? `${summary.total.toLocaleString("en-IN")} leads` : "…"}
        </span>
        {summary && ["Hot", "Warm", "Cold"].filter((r) => summary.by_rating?.[r]).map((r) => (
          <span key={r} className={`inline-flex items-center rounded-md border px-2 py-1.5 text-[11px] font-medium whitespace-nowrap ${r === "Hot" ? "bg-red-50 border-red-100 text-red-700" : r === "Warm" ? "bg-amber-50 border-amber-100 text-amber-700" : "bg-sky-50 border-sky-100 text-sky-700"}`} data-testid={`leads-chip-rating-${r.toLowerCase()}`}>
            <span className="font-semibold mr-1">{summary.by_rating[r]}</span>{r}
          </span>
        ))}
        {summary && Object.keys(summary.by_rating || {}).length > 0 && (
          <span className="h-5 w-px bg-slate-200 mx-0.5" />
        )}
        {summary && LEAD_STATUSES.filter((s) => summary.by_status?.[s]).map((s) => (
          <span key={s} className="inline-flex items-center rounded-md bg-white border border-slate-200 px-2 py-1.5 text-[11px] font-medium text-slate-500 whitespace-nowrap" data-testid={`leads-chip-status-${s.replace(/\s+/g, "-").toLowerCase()}`}>
            <span className="font-semibold text-slate-700 mr-1">{summary.by_status[s]}</span>{s}
          </span>
        ))}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, company, phone…" className="h-9 pl-9" data-testid="lead-search" />
        </div>
        {(needsCount.data?.total > 0 || needsDetails) && (
          <button onClick={() => { const v = !needsDetails; setNeedsDetails(v); setPage(1); if (v) setView("table"); }}
            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-xs font-medium transition-colors ${needsDetails ? "bg-amber-500 border-amber-500 text-white" : "bg-amber-50 border-amber-200 text-amber-800 hover:border-amber-400"}`}
            title="Exhibition / scanned leads still missing Value, EDC, Zone or Branch" data-testid="lead-needs-details-chip">
            <AlertTriangle className="h-3.5 w-3.5" /> Needs details <span className="font-mono">{needsCount.data?.total ?? 0}</span>
          </button>
        )}
        {view === "table" && (
          <>
            <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All statuses</SelectItem>{dd?.lead_status?.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={rating || "all"} onValueChange={(v) => setRating(v === "all" ? "" : v)}>
              <SelectTrigger className="h-9 w-[130px]" data-testid="filter-rating"><SelectValue placeholder="Rating" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All ratings</SelectItem>{dd?.rating?.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={region || "all"} onValueChange={(v) => setRegion(v === "all" ? "" : v)}>
              <SelectTrigger className="h-9 w-[130px]" data-testid="filter-region"><SelectValue placeholder="Region" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All regions</SelectItem>{dd?.region?.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={productType || "all"} onValueChange={(v) => setProductType(v === "all" ? "" : v)}>
              <SelectTrigger className="h-9 w-[160px]" data-testid="filter-product-type"><SelectValue placeholder="Type of Product" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All product types</SelectItem>{(dd?.product_type || PRODUCT_TYPES).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <SalespersonFilter value={owner || "all"} onChange={(v) => { setOwner(v === "all" ? "" : v); setPage(1); }} testid="lead-salesperson-filter" />
            <EventFilter entity="leads" value={event} onChange={(v) => { setEvent(v); setPage(1); }} divisionParam={divisionParam} testid="lead-event-filter" />
          </>
        )}
      </div>

      {!dealerView && ["super_admin", "sales_head", "management"].includes(user?.role) && <LeadOwnerSummary rows={summary?.by_owner} />}

      {view === "kanban" ? (
        kanban.isLoading ? <Loading /> : (
          <KanbanBoard
            columns={columns}
            itemsByColumn={kanban.data?.columns || {}}
            getId={(l) => l.id}
            onMove={move}
            renderCard={(lead) => <LeadCard lead={lead} isGlobal={isGlobal} user={user} onDelete={del} onClick={() => navigate(`/leads/${lead.id}`)} />}
          />
        )
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
          {canBulkEmail && sel.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-indigo-50 border-b border-indigo-100 text-sm" data-testid="lead-bulk-bar">
              <span className="font-medium text-indigo-900">{sel.length} lead{sel.length === 1 ? "" : "s"} selected</span>
              <Button size="sm" className="h-8 gap-1.5 bg-slate-900 hover:bg-slate-800" onClick={() => setSendOpen(true)} data-testid="lead-send-catalogues-btn"><Send className="h-3.5 w-3.5" /> Send catalogues</Button>
              <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => setBrevoOpen(true)} data-testid="lead-add-brevo-btn"><UploadCloud className="h-3.5 w-3.5" /> Add to Brevo list</Button>
              <button onClick={() => setSel([])} className="ml-auto text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="lead-clear-selection"><X className="h-3.5 w-3.5" /> Clear</button>
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {canBulkEmail && <TableHead className="w-8"><input type="checkbox" className="h-3.5 w-3.5 accent-slate-900" checked={items.length > 0 && items.every((l) => sel.includes(l.id))} onChange={(e) => setSel(e.target.checked ? [...new Set([...sel, ...items.map((l) => l.id)])] : sel.filter((id) => !items.some((l) => l.id === id)))} data-testid="lead-select-all" /></TableHead>}
                  {["Name", "Company", "Status", "Rating", "Data", "Region", "Owner", "Created", isGlobal && "Division"].filter(Boolean).map((h) => (
                    <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>
                  ))}
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((l) => (
                  <TableRow key={l.id} className={`cursor-pointer ${sel.includes(l.id) ? "bg-indigo-50/40" : ""}`} onClick={() => navigate(`/leads/${l.id}`)} data-testid={`lead-row-${l.id}`}>
                    {canBulkEmail && <TableCell onClick={(e) => e.stopPropagation()}><input type="checkbox" className="h-3.5 w-3.5 accent-slate-900" checked={sel.includes(l.id)} onChange={() => setSel((p) => (p.includes(l.id) ? p.filter((x) => x !== l.id) : [...p, l.id]))} data-testid={`lead-select-${l.id}`} /></TableCell>}
                    <TableCell className="font-medium max-w-[200px] truncate" title={l.project_name || l.name || ""}>{l.project_name || l.name}{l.is_sample && <span className="ml-1.5 text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">Sample</span>}</TableCell>
                    <TableCell className="text-slate-600 max-w-[180px] truncate" title={l.company || ""}>{l.company}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()} className="whitespace-nowrap">
                      <LeadStatusSelect leadId={l.id} value={l.lead_status} lostReason={l.lost_reason} size="xs" className="w-[140px]"
                        onChanged={() => { qc.invalidateQueries({ queryKey: ["leads-table"] }); qc.invalidateQueries({ queryKey: ["leads-kanban"] }); }} />
                    </TableCell>
                    <TableCell>{l.rating !== "None" ? <StatusBadge kind="rating" value={l.rating} /> : <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell><CompletenessBadge completeness={l.completeness} compact /></TableCell>
                    <TableCell className="text-slate-600 whitespace-nowrap">{l.region || "—"}</TableCell>
                    <TableCell className="text-slate-600 whitespace-nowrap max-w-[140px] truncate" title={l.owner_name || ""}>{l.owner_name}</TableCell>
                    <TableCell className="text-slate-500 text-sm whitespace-nowrap">{formatDate(l.created_at)}</TableCell>
                    {isGlobal && <TableCell><DivisionTag id={l.division_id} /></TableCell>}
                    <TableCell onClick={(e) => e.stopPropagation()} className="text-right">
                      <span className="inline-flex items-center gap-1.5">
                        <AssignOwnerSelect entityType="lead" entityId={l.id} ownerId={l.owner_id} compact
                          onAssigned={() => { qc.invalidateQueries({ queryKey: ["leads-table"] }); qc.invalidateQueries({ queryKey: ["leads-kanban"] }); }} />
                        {canDeleteLeadDeal(user) && (
                          <DeleteButton testId={`lead-delete-${l.id}`} title="Delete this lead?" description="This permanently removes the lead and its activity history." onConfirm={() => del(l.id)} />
                        )}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
                {table.data?.items?.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-12 text-slate-400">No leads found</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
          {table.data && table.data.total > 25 && (
            <div className="flex items-center justify-between px-4 py-3 border-t text-sm">
              <span className="text-slate-500">{table.data.total} leads</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)} data-testid="page-prev">Prev</Button>
                <Button variant="outline" size="sm" disabled={page * 25 >= table.data.total} onClick={() => setPage((p) => p + 1)} data-testid="page-next">Next</Button>
              </div>
            </div>
          )}
        </div>
      )}

      <LeadFormDialog open={formOpen} onOpenChange={(v) => { setFormOpen(v); if (!v) setVoiceInitial(null); }} initial={voiceInitial} onSaved={() => { qc.invalidateQueries({ queryKey: ["leads-kanban"] }); qc.invalidateQueries({ queryKey: ["leads-table"] }); }} />
      <SendCataloguesDialog open={sendOpen} onOpenChange={setSendOpen} leadIds={sel} onDone={() => setSel([])} />
      <AddToBrevoDialog open={brevoOpen} onOpenChange={setBrevoOpen} leadIds={sel} onDone={() => setSel([])} />
      <ReasonDialog open={!!reasonMove} onOpenChange={(o) => !o && setReasonMove(null)} kind={reasonMove?.kind || "lost"}
        title={reasonMove?.kind === "won" ? `Lead won — “${reasonMove?.toStatus}”` : `Mark lead as “${reasonMove?.toStatus}”`}
        onConfirm={(r, c) => { const m = reasonMove; setReasonMove(null); move(m.id, m.toStatus, m.kind === "won" ? { won_reason: r, won_comments: c } : { lost_reason: r, lost_comments: c }); }} />
    </div>
  );
}

function Loading() {
  return <div className="flex gap-4">{[1, 2, 3, 4].map((i) => <div key={i} className="w-72 h-64 rounded-lg bg-slate-100 animate-pulse" />)}</div>;
}
